import Crosshair from './Crosshair';
import { types as eventTypes } from './enums/event';
import { types as cursorTypes } from './enums/cursor';
import Modcharts from './Modcharts';
import Tool from './Tool';
import toolMap from './tools';
import EdgeHandle from './tools/EdgeHandle';
import Handle from './tools/Handle';
import LineTool from './tools/LineTool';
import OHLCCrosshairTool from './tools/OHLCCrosshairTool';
import ReturnsCalculatorTool from './tools/ReturnsCalculatorTool';
import SnapHandle from './tools/SnapHandle';
import eventUtil from './util/events';
import zoomUtil from './util/zoom';

export default class ModchartsInteractive extends Modcharts {
	constructor(rootSelector, params = {}, parent = null) {
		super(rootSelector, params, parent);
		// Use default params for default crosshair
		this.crosshair = new Crosshair(this);
	}

	initDOM() {
		super.initDOM();

		// root mouse
		this.rootMouse
			.on('mouseover', event => {
				return this.onMouseover(event);
			})
			.on('mouseout', event => {
				return this.onMouseout(event);
			})
			.on('mousemove', event => {
				return this.onMousemove(event);
			})
			.on('click', event => {
				return this.onClick(event);
			})
			.on('dblclick', event => {
				return this.onDoubleClick(event);
			})
			.on('contextmenu', event => {
				return this.onRightClick(event);
			})
			.on('mousedown', event => {
				return this.onMousedown(event);
			})
			.on('mouseup', event => {
				return this.onMouseup(event);
			})
			.on('touchstart', event => {
				return this.onTouchStart(event);
			})
			.on('touchend', event => {
				return this.onTouchEnd(event);
			})
			.on('touchmove', event => {
				return this.onTouchMove(event);
			});

		// Add event listeners for keyboard, use encapsulating functions to perserve scope
		this._onKeyDown = event => { this.onKeyDown(event); };
		document.body.addEventListener('keydown', this._onKeyDown);
		this._onKeyUp = event => { this.onKeyUp(event); };
		document.body.addEventListener('keyup', this._onKeyUp);
	}

	resize() {
		super.resize();

		if (!this.params.size) {
			this.rootMouse.style('width', `${this.size.width}px`);
			this.rootMouse.style('height', `${this.size.height}px`);
		}
	}

	setDateRange(dateStart, dateStop) {
		// unselect tools and exit tool mode
		this.selectTool(null);
		this.setToolMode(false);

		super.setDateRange(dateStart, dateStop);
	}

	set days(days) {
		// Unselect tools and exit tool mode
		this.selectTool(null);
		this.setToolMode(false);

		// Reset the scale extent so that it gets recalculated
		this.zoom.scaleExtent([0, Infinity]);

		// Call super's days setter
		super.days = days;

		// Update the zoom center due to xAxis changing
		this.state.updateZoomCenter = true;
	}

	/**
	 * set new primary symbol and reset price indicators params.symbol
	 * @param {string} symbol
	 */
	setSymbol(symbol) {
		super.setSymbol(symbol);
		// unselect tools and exit tool mode
		this.selectTool(null);
		this.setToolMode(false);
	}
	/** setter for params.toolSnapOHLC which also removes the snapHandle reference */
	setToolSnapOHLC(isEnabled) {
		this.params.toolSnapOHLC =
			typeof isEnabled === 'boolean' ? isEnabled : true;

		if (!this.params.toolSnapOHLC) {
			this.state.snapHandle = null;
		}
	}
	/**
	 * Initializes tool instances from saved tool Object
	 * @method getToolsFromJSON
	 * @params {object} panel
	 * @params {array} tools
	 */
	getToolsFromJSON(panel, savedTools = []) {
		const tools = [];
		for (let t = 0; t < savedTools.length; t++) {
			const savedTool = savedTools[t];
			const ToolClass = Tool.getToolFromType(savedTool.params.id, toolMap);

			if (ToolClass) {
				const tool = new ToolClass(this, savedTool.params);
				tool.init(panel);

				for (const handleJSON of savedTool.handle) {
					tool.handle.push(
						Handle.fromJSON(handleJSON, tool)
					);
				}

				tools.push(tool);
			}
		}
		return tools;
	}

	/** Mouse click fired.  based on current chart state, call various click handlers */
	onClick(event, args = {}) {
		const { isAutomatic, isImmediate } = args;
		let mouse = eventUtil.getMouseFromDomEvent(event);
		const el = event && (event.target || event.srcElement);

		if (el) {
			if (event.cancelable) event.preventDefault();
			event.stopPropagation();
		} else if (
			(
				isAutomatic ||
				isImmediate
			) &&
			this.panels.length
		) {
			const { size } = this.panels[0];
			mouse = [size.padding.left + 1, size.padding.top + size.top + 1];
		} else {
			return;
		}
		const activePanel = this.getActivePanel(mouse);
		this.eventEmitter.emit(eventTypes.CLICK_START);

		if (!activePanel) return;

		if (this.state.preventClick) {
			this.state.preventClick = null;
			return;
		}

		this.removeStateTools();
		this.untrackTouches();

		const { tool } = this.state;
		if (tool) {
			// we're in tool mode, so check if we're waiting for the first click or second click.
			if (tool.state && tool.state.inProgressHandle) {
				// currently in the middle of clicking handles for an in-progress tool (such as between two clicks)
				tool.onClickCanvas(mouse); // Pass in mouse coords for extended horizontal lines (CH-1466)
			} else {
				// first click, let's start a new tool
				tool.init(activePanel);
				if (tool.isImmediate) {
					// When a tool isImmediate, it is immediately created on click of the tool button, so it needs
					// to stay invisible until the the cursor enters the panel and produces usable coordinates.
					tool.state.visible = false;
				}
				activePanel.tools.push(tool);
				this.selectTool(tool);
				tool.onClickCanvas(mouse);
			}
			activePanel.clearTools();
			activePanel.renderTools();
		} else {
			// it was a click on the canvas, make sure all tools are deselected
			this.selectTool(null);
		}

		this.eventEmitter.emit(eventTypes.CLICK, { mouse });
	}

	/** Return price/date information about click.  return info for both mouse and priceline */
	getClickInfo(mouse) {
		const activePanel = this.getActivePanel(mouse);

		if (activePanel?.index === 0) {
			const y = mouse[1];
			const scaleIdx = Math.round(
				this.panels[0].xAxis.primaryScale.invert(mouse[0])
			);
			const datapointIdx = this._getIndexByDomain(
				scaleIdx,
				this.dataPrimary,
				'dateIndex'
			);
			const date = new Date(this.exchangeDates[scaleIdx]);
			const datapointValue = this.dataPrimary[datapointIdx].close;
			const x = Math.round(this.xAxis.primaryScale(scaleIdx));
			const datapointY = this.panels[0].yAxis.primaryScale(datapointValue);
			const value = this.panels[0].yAxis.primaryScale.invert(mouse[1]);

			return {
				mouse: { x, y, date, value },
				datapoint: {
					x,
					y: datapointY,
					date,
					value: datapointValue
				}
			};
		}
		return null;
	}

	/** Mouse right click fired. */
	onRightClick(event) {
		const mouse = eventUtil.getMouseFromDomEvent(event);
		const activePanel = this.getActivePanel(mouse);

		if (!activePanel) return;

		const valIdx = Math.round(activePanel.xAxis.primaryScale.invert(mouse[0]));
		const date = new Date(this.exchangeDates[valIdx]);
		const value = activePanel.yAxis.primaryScale.invert(mouse[1]);

		this.eventEmitter.emit(eventTypes.CLICK_RIGHT, {
			date,
			value,
			mouse,
			event
		});
	}

	/**
	 * Mouse down fired.
	 * if we were hovering over a panel resize handle, enter resizing state
	 * call activepanel's tool's mousedown handlers
	 * else call active panel's mousedown handler
	 * mouseIn can be used to manually override the mouse coordinates
	 * touchId will be passed in if this call came from the touchStart handler.
	 */
	onMousedown(event, mouseIn, touchId) {
		this.eventEmitter.emit(eventTypes.MOUSE_DOWN);
		if (event.cancelable) {
			event.preventDefault();
			event.returnValue = false;
		}
		event.stopPropagation();

		if (this.status === 0) return;

		const mouse = mouseIn || eventUtil.getMouseFromDomEvent(event);

		if (this.state.hoverResizePanel) {
			// we were hovering over a panel resizer, and moused down.  begin the panel resizing process.
			this.state.resizePanel = this.state.hoverResizePanel;
			zoomUtil.unregisterZoom(this);
			this.state.resizePanelY = mouse[1];
		} else {
			// determine if we should begin any tool actions (drag handle or drag tool)
			const activePanel = this.getActivePanel(mouse);

			if (activePanel) {
				// call tool's mousedown handler
				for (const tool of activePanel.tools) {
					tool.onMousedown(mouse, touchId);
				}
			}
		}

		return false;
	}

	/**
	 * Main chart mousemove handler
	 * if a tool is in progress, fire its onmousemove handler
	 * a tool is "inProgressHandle" state when it's in the middle of a two- or three-chord click action and we're not yet done.
	 * if no tools are in progress but we are in tool mode and just moused over a handle, fire its move handler
	 * if no tools are in progress and we're not in the middle of zooming (pan/zoom), update the crosshair
	 */
	onMousemove(event, touchId) {
		this.eventEmitter.emit(eventTypes.MOUSE_MOVE);
		if (this.state.zooming || this.dataXHR || this.status === 0) return;

		const mouse = eventUtil.getMouseFromDomEvent(event);
		const activePanel = this.getActivePanel(mouse);
		// throttle mousemove events - chromium continues to fire mousemove during resting state
		// see https://code.google.com/p/chromium/issues/detail?id=170631
		if (
			this.state.prevMouse &&
			this.state.prevMouse[0] === mouse[0] &&
			this.state.prevMouse[1] === mouse[1]
		) {
			if (event.cancelable) event.preventDefault();
			event.stopPropagation();

			return false;
		}

		// track last mouse position
		this.state.prevMouse = [...mouse];
		// reset the cursor and exit when no longer on a panel
		if (
			!activePanel &&
			!this.state.resizePanel &&
			// Check if this is a touch event
			!this.state.isTouchDevice
		) {
			this.eventEmitter.emit(eventTypes.MOUSE_LEAVE);
			this.setCursor(cursorTypes.DEFAULT);

			// Don't listen for zoom events if not on a panel chart
			zoomUtil.unregisterZoom(this);

			return;
		}
		// Start listening for zoom events
		zoomUtil.registerZoom(this);

		if (this.state.resizePanel) {
			this.handleMousemovePanelResize(mouse);
		} else if (
			this.state.tool &&
			this.state.tool.state &&
			this.state.tool.state.inProgressHandle
		) {
			// in the middle of dragging a tool handle
			this.handleMousemoveActiveToolHandle(event, touchId);
		} else if (this.state.tool && this.state.dragTool) {
			// in the middle of dragging a tool
			this.handleMousemoveActiveTool(mouse);
		} else if (this.handleMousemoveToolDefault(mouse)) {
			// mousing over a tool
			return;
		} else if (this.params.hoverIndicator && this.handleMousemoveIndicator(mouse)) {
			// mousing over an indicator
			return;
		} else {
			// default mousemove handler
			this.handleMousemoveDefault(event, activePanel);
		}

		return false;
	}

	/** Default double-click handler, also detects if the double-click was on a tool */
	onDoubleClick(event) {
		const mouse = eventUtil.getMouseFromDomEvent(event);
		const clickInfo = this.getClickInfo(mouse);
		const tool = this._isWithinTool(mouse);
		if (clickInfo) {
			this.eventEmitter.emit(eventTypes.DOUBLE_CLICK, {
				datapoint: clickInfo.datapoint
			});
		}
		if (tool) {
			this.eventEmitter.emit(eventTypes.TOOL_DOUBLE_CLICK, {
				...tool.params
			});
		}
		return false;
	}

	/**
	 * User is in the middle of resizing a panel
	 * @param {Number[]} mouse - Mouse position information [x, y]
	 */
	handleMousemovePanelResize(mouse) {
		const deltaPct = (mouse[1] - this.state.resizePanelY) / this.size.height;
		const minHeight = 0.05;
		let panelUp;
		let panelDown;

		for (let p = 1; p < this.panels.length; p++) {
			panelUp = this.panels[p - 1];
			panelDown = this.panels[p];

			if (
				panelDown === this.state.resizePanel &&
				panelUp.size.heightPct + deltaPct > minHeight &&
				panelDown.size.heightPct - deltaPct > minHeight
			) {
				panelUp.size.heightPct += deltaPct;
				panelDown.size.heightPct -= deltaPct;

				panelUp.params.size.heightPct = panelUp.size.heightPct;
				panelDown.params.size.heightPct = panelDown.size.heightPct;

				break;
			}
		}

		this.state.resizePanelY = mouse[1];
		this.resize();
		this.render();
	}

	/**
	 * User is mousing over the chart with no other interaction.  also called when in toolmode
	 * but haven't started drawing yet.
	 * @param {Number[]} mouse - Mouse position information [x, y]
	 * @param {Object} activePanel - Panel that mouse is moving over
	 */
	handleMousemoveDefault(event, activePanel) {
		const mouse = eventUtil.getMouseFromDomEvent(event);
		const prevEventHover = this.state.eventHover;
		let overResizeHandle = false;

		this.eventEmitter.emit(eventTypes.MOUSE_MOVE);

		this.state.eventHover = null;

		// User is mousing over an event icon
		for (const panel of this.panels) {
			for (const panelEvent of panel.events) {
				const eventWithin = panelEvent.isWithin(mouse);

				if (eventWithin !== null) {
					this.state.eventHover = eventWithin;
					break;
				}
			}
			if (this.state.eventHover) break;
		}

		// there was an event hover but now there is none.
		if (prevEventHover && !this.state.eventHover) {
			this.eventEmitter.emit(eventTypes.EVENT_MOUSE_LEAVE, { previousEventHover: prevEventHover });
		} else if (
			this.state.eventHover !== null &&
			this.state.eventHover !== prevEventHover
		) {
			this.eventEmitter.emit(eventTypes.EVENT_MOUSE_ENTER, { event: this.state.eventHover });
		}

		if (this.params.panelResize) {
			// user is mousing over a panel resize handle
			const firstTop = this.panels[0].params.style.padding.top;

			for (const panel of this.panels) {
				// don't activate handle on first panel or fixed-height panels
				if (mouse[1] > firstTop + 5 && !this.params.panelHeightLower) {
					const { top } = panel;

					if (mouse[1] > top - 6 && mouse[1] < top + 5) {
						this.state.hoverResizePanel = panel;
						overResizeHandle = true;
						break;
					}
				}
			}
		}

		if (overResizeHandle) {
			// mousing over resize handle
			this.setCursor(cursorTypes.NS_RESIZE);
			this.eventEmitter.emit(eventTypes.RESIZE_HANDLE_HOVER);
		} else if (activePanel?.isWithin(mouse)) {
			const { isUpper } = activePanel;

			// within chart region.  regular mouseover with crosshair active
			this.state.hoverResizePanel = null;

			// set cursor to cross if we are not hovering over an event
			if (!this.state.eventHover) this.setCursor(cursorTypes.CROSSHAIR);

			// create a snap handle
			if (
				isUpper &&
				!this.state.snapHandle &&
				this.params.toolSnapOHLC
			) {
				this.state.snapHandle = new SnapHandle(this, activePanel);
			}

			if (
				isUpper &&
				this.params.toolSnapOHLC &&
				this.state.tool
			) {
				// clear tools canvas
				activePanel.clearTools();

				// re-render
				activePanel.renderTools();

				// snap handle
				this.state.snapHandle.render(event);
			} else {
				this.eventEmitter.emit(eventTypes.PANEL_MOUSE_MOVE, { mouse, activePanel });

				for (const tool of activePanel.tools) {
					if (tool.isImmediate && !tool.state.visible) {
						tool.state.visible = true;
						tool.state.inProgressHandle = true;
						tool.state.selected = true;
						tool.state.dragHandle = tool.handle[0];
						tool.render(event);

						this.state.tool = tool;
						this.onMousedown(event, mouse);

						this.eventEmitter.emit(eventTypes.TOOL_MOUSE_MOVE, event, mouse);

						return;
					}
				}
			}
		} else {
			// not yet within chart region
			this.state.hoverResizePanel = null;

			this.setCursor(cursorTypes.DEFAULT);
			this.eventEmitter.emit(eventTypes.MOUSE_LEAVE);
		}
	}

	/**
	 * user is mousing over the toolmode chart.  set cursor and hover state of tools
	 * @param {Number[]} mouse - Mouse position information [x, y]
	 */
	handleMousemoveToolDefault(mouse) {
		let cursor = cursorTypes.CROSSHAIR;
		const hoverTool = this._isWithinTool(mouse);
		let isRender = false;
		let isEnter = false;
		let isHandleEnter = false;

		// call onmouseout on all non-hovered tools
		this.panels.forEach(panel => {
			panel.tools.forEach(tool => {
				if (
					(!hoverTool || tool !== hoverTool) &&
					tool.state.hover
				) {

					this.onToolMouseout(tool);

					tool.handle.forEach(handle => {
						// fire off the handle mouse leave event.  just to be safe, fire it indiscriminately for all non-hovered handles.
						const mousePanel = [mouse[0], mouse[1] - panel.size.top];
						this.onToolHandleMouseout(handle, mousePanel);
					});

					// this removes the little gray circles from the handles
					if (tool.state.hover) {
						tool.state.hover = false;
						zoomUtil.registerZoom(this);
					}

					isRender = true;
				}
			});
		});

		// call onmouseover of hovered tool
		this.panels.forEach(panel => {
			panel.tools.forEach(tool => {
				if (
					hoverTool &&
					tool === hoverTool &&
					!tool.state.hover
				) {

					// handles
					tool.handle.forEach(handle => {
						// we're not necessarily over a handle just because we're over a tool, so check for that.
						const mousePanel = [mouse[0], mouse[1] - panel.size.top];
						if (handle.isWithin(mousePanel)) {
							isHandleEnter = true;
							this.onToolHandleMouseover(handle, mousePanel);
						}
					});

					// setting the tool state to hover will enables the little gray circles on handles.
					// we want these to show up immediately for both handles when hovering over a tool.
					if (!tool.state.hover) {
						tool.state.hover = true;
						tool.handle.forEach(handle => {
							handle.state.hover = true;
						});
						zoomUtil.unregisterZoom(this); // disable pan/zoom when hovering over handle
					}

					// tool
					cursor = cursorTypes.POINTER;
					if (!isHandleEnter) {
						isEnter = true;
						this.onToolMouseover(tool, mouse);
					}
					isRender = true;
				}
			});
		});

		// call mousemove of hovered tool.  this goes last so mouseover can fire first
		if (hoverTool && !isEnter && !isHandleEnter) {
			cursor = cursorTypes.POINTER;
			this.eventEmitter.emit(eventTypes.TOOL_MOUSE_MOVE, hoverTool, mouse);

			isRender = true;
		}

		// re-render tools if hover state changed
		if (isRender) {
			this.setCursor(cursor);

			// render tool canvases
			this.panels.forEach(panel => {
				if (panel.tools.length) {
					panel.clearTools();
					panel.renderTools();
				}
			});
		}

		return hoverTool;
	}

	/**
 * handle mouseover of indicators
 * @param {Number[]} mouse - Mouse position information [x, y]
 */
	handleMousemoveIndicator(mouse) {
		let indicatorHover = null;
		const panel = this.panels[0];

		// Determine the topmost indicator that is hovered
		for (let i = panel.indicators.length - 1; i >= 0; i--) {
			const indicator = panel.indicators[i];
			if (indicator.state) {
				const indicatorWithinData = indicator.isWithin(mouse);
				if (indicatorWithinData) {
					indicatorHover = indicator;
					break; // Stop at the first indicator that is hovered
				}
			}
		}

		// Update hover states and emit events
		panel.indicators.forEach(indicator => {
			if (indicator.state) {
				const origHoverState = indicator.state.hover;
				indicator.state.hover = (indicator === indicatorHover);

				if (indicator.state.hover) {

					if (origHoverState !== indicator.state.hover && indicator.hoverEventOver) {
						// Emit INDICATOR_MOUSE_OVER event
						panel.core.eventEmitter.emit(
							indicator.hoverEventOver, {
								params: { ...indicator.params },
								coords: { x: mouse[0], y: mouse[1] }
							}
						);
					}

					// Emit INDICATOR_MOUSE_MOVE event
					panel.core.eventEmitter.emit(
						'INDICATOR_MOUSE_MOVE', {
							params: { ...indicator.params },
							coords: { x: mouse[0], y: mouse[1] }
						}
					);
				} else {
					if (origHoverState && indicator.hoverEventOut) {
						// Emit INDICATOR_MOUSE_OUT event
						panel.core.eventEmitter.emit(
							indicator.hoverEventOut, {
								params: { ...indicator.params },
								coords: { x: mouse[0], y: mouse[1] }
							}
						);
					}
				}
			}
		});

		return indicatorHover;
	}


	/**
	 * a tool is currently in the middle of drawing
	 * @param {Number[]} mouse - Mouse position information [x, y]
	 */
	handleMousemoveActiveTool(mouse, touchId) {
		this.state.tool.onMousemove(mouse, touchId);

		this.panels.forEach(panel => {
			panel.clearTools();
			panel.renderTools();
		});
	}

	/**
	 * a tool handle is being modified
	 * @param {Number[]} mouse - Mouse position information [x, y]
	 */
	handleMousemoveActiveToolHandle(event, touch) {
		const mouse = eventUtil.getMouseFromDomEvent(event);
		this.state.tool.onMousemove(mouse, touch, event);

		this.panels.forEach(panel => {
			panel.clearTools();
			panel.renderTools();
		});

		// snap handle
		if (
			this.state.snapHandle &&
			this.state.tool &&
			this.state.tool.shouldSnap
		) {
			this.state.snapHandle.render(event);
		}
	}

	/**
	 * onMouseout of entire chart itself
	 * @method onMouseout
	 */
	onMouseout(event) {
		this.eventEmitter.emit(eventTypes.MOUSE_LEAVE, event);
		this.setCursor(cursorTypes.DEFAULT);
	}

	/**
	 * onMouseover of entire chart itself
	 * @method onMouseover
	 */
	onMouseover(event) {
		this.eventEmitter.emit(eventTypes.MOUSE_ENTER, event);
	}

	/**
	 * onMouseover of a tool object
	 * @method onToolMouseover
	 */
	onToolMouseover(tool, mouse) {
		this.eventEmitter.emit(eventTypes.TOOL_MOUSE_ENTER, { tool, mouse });
	}

	/**
	 * onMouseout of a tool object
	 * @method onToolMouseout
	 */
	onToolMouseout(tool) {
		this.eventEmitter.emit(eventTypes.TOOL_MOUSE_LEAVE, { tool });
	}

	/**
	 * onMouseover of a handle object
	 * @method onToolHandleMouseover
	 */
	onToolHandleMouseover(handle, mouse) {
		handle.state.hover = true;
		this.eventEmitter.emit('TOOL_HANDLE_MOUSE_ENTER', { handle, mouse });
	}

	/**
	 * onMouseout of a handle object
	 * @method onToolHandleMouseout
	 */
	onToolHandleMouseout(handle, mouse) {
		handle.state.hover = false;
		this.eventEmitter.emit('TOOL_HANDLE_MOUSE_LEAVE', { handle, mouse });
	}

	/**
	 * tool is rendered
	 * @method onToolRender
	 */
	onToolRender(tool) {
		this.eventEmitter.emit(eventTypes.TOOL_RENDER, { tool });
	}

	/**
	 * onMousedown of a tool object (or tool handle)
	 * @method onToolMousedown
	 */
	onToolMousedown(tool) {
		this.eventEmitter.emit(eventTypes.TOOL_MOUSE_DOWN, { tool });

		// stop tracking finger1/finger2 for longpresses
		this.untrackTouches();
	}

	/**
	 * onMouseup of a tool object (or tool handle)
	 * @method onToolMouseup
	 */
	onToolMouseup(tool) {
		this.eventEmitter.emit(eventTypes.TOOL_MOUSE_UP, { tool });
	}

	/**
	 * onMousemove of a tool object (or tool handle)
	 * @method onToolMousemove
	 */
	onToolMousemove(tool, mouse) {
		this.eventEmitter.emit(eventTypes.TOOL_MOUSE_MOVE, { tool, mouse });
	}

	/**
	 * handle backspace/delete to remove tools (needs to be onkeydown instead of onkeyup
	 * to prevent the browser's "back button" action)
	 * @method onKeyDown
	 */
	onKeyDown(event) {
		this.eventEmitter.emit(eventTypes.KEY_DOWN, event);

		if (this.params.removeToolByKey && event) {
			const keyCode = event.keyCode;

			if (keyCode === 8 || keyCode === 46) {
				// backspace || delete

				const d = event.target || event.srcElement || {};
				const tagName = d.tagName ? d.tagName.toLowerCase() : '';
				const tagType = d.type ? d.type.toLowerCase() : '';
				const allowDefault =
						tagName === 'textarea' ||
						(tagName === 'input' &&
							/text|number|password|file|email|search|date|tel/.test(
								tagType
							));

				if (!allowDefault) {
					const selectedTool = this.getSelectedTool();

					if (selectedTool) {
						// only trap event if we have a selected tool
						event.preventDefault();

						selectedTool.remove();
						this.setCursor(cursorTypes.CROSSHAIR);

						this.eventEmitter.emit(eventTypes.TOOL_REMOVE);
					}
				}
			}
		}
	}

	/**
	 * @method onKeyUp
	 */
	onKeyUp(event) {
		this.eventEmitter.emit(eventTypes.KEY_UP, event);
	}

	/** Getter to be used in args returned by BOUNDARY_STATUS event */
	get _boundaryStatus() {
		return {
			isAtLeftBoundary: this.isAtLeftBoundary,
			isAtRightBoundary: this.isAtRightBoundary,
			isAtZoomInBoundary: this.isAtZoomInBoundary,
			isAtZoomOutBoundary: this.isAtZoomOutBoundary
		};
	}

	loadingStop(emitBoundaryStatus = true) {
		super.loadingStop();

		// Only emit this event if there's no error
		if (emitBoundaryStatus) this.eventEmitter.emit(eventTypes.BOUNDARY_STATUS, this._boundaryStatus);
	}

	/** Determines if chart has been panned/zoomed to left boundary */
	get isAtLeftBoundary() {
		if (this.xAxis) {
			return Math.floor(this.xAxis.primaryScale.domain()[0]) <= 0;
		}
		this.warn('XAxis scale is not available yet');
		return false;
	}

	/** Determines if chart has been panned/zoomed to right boundary */
	get isAtRightBoundary() {
		if (this.xAxis) {
			return Math.ceil(this.xAxis.primaryScale.domain()[1]) >= this._calcLastIndex();
		}
		this.warn('XAxis scale is not available yet');
		return false;
	}

	/** Determines if chart has been zoomed in to the limit */
	get isAtZoomInBoundary() {
		if (this.xAxis) {
			// Is the current scale greater than (zoomed in) or equal to the scale limit (extent)?
			return this.zoomUtil.getZoomTransform(this).k >= this.zoom.scaleExtent()[1];
		}
		this.warn('XAxis scale is not available yet');
		return false;
	}

	/** Determines if chart has been zoomed out to the limit */
	get isAtZoomOutBoundary() {
		if (this.xAxis) {
			// The chart is zoomed completely out if at the left and right boundaries
			return this.isAtLeftBoundary && this.isAtRightBoundary;
		}
		this.warn('XAxis scale is not available yet');
		return false;
	}

	/** Default mouseup event handler */
	onMouseup() {
		this.eventEmitter.emit(eventTypes.MOUSE_UP);

		this.state.resizePanel = null;
		this.state.hoverResizePanel = null;

		if (this.state.dragTool) {
			this.state.preventClick = true;
			this.state.dragTool.onMouseup();
		}
	}

	/** Manually fire onMousedown and onMousemove (once) when onTouchStart fires */
	onTouchStart(event) {
		this.eventEmitter.emit(eventTypes.TOUCH_START);

		// Store touch device check for use later
		if (!this.state.isTouchDevice) {
			this.state.isTouchDevice = true;

			const bodyTouchCallback = touchEvent => {
				if (touchEvent.target !== this.rootMouse.node()) {
					this.eventEmitter.emit(eventTypes.MOUSE_LEAVE);
				}
			};

			// Add body listener (and callback) if one doesn't exist (CH-1601)
			if (!this.body.on('touchstart')) this.body.on('touchstart', bodyTouchCallback);
		}

		const touches = event.changedTouches;
		Object.keys(touches).forEach(idx => {
			const row = touches[idx];
			// begin tracking this unique touch
			const offset = this.panels[0].rootCanvas.node().getBoundingClientRect();
			const x = Math.round(row.pageX - offset.left);
			const y = Math.round(row.pageY - offset.top);

			this.trackTouch(row.identifier, {
				id: row.identifier,
				offset, // so we don't have to recalculate it in the touchmove handler
				x, // xy relative to panel
				y, // xy relative to panel
				newX: x, // xy relative to panel
				newY: y, // xy relative to panel
				date: new Date().getTime()
			});
		});

		// start a timer if either of the long-press tools are enabled and not active
		if (
			!this.state.toolOHLCCrosshair &&
			!this.state.toolReturnsCalculator &&
			(
				this.params.toolOHLCCrosshairEnabled ||
				this.params.toolReturnsCalculatorEnabled
			)
		) {
			const longPressDuration = 600;
			const maxDriftDistance = 25;

			window.clearTimeout(window.pressTimeout);

			// start timer
			window.pressTimeout = window.setTimeout(() => {
				// timer has elapsed, check distance to see if we have a valid longPress or longDoublePress
				let distance = Infinity;
				let distance2 = Infinity;
				const getDistance = LineTool.prototype.distance;
				const mytouches = [];

				// filter to a numeric array of valid touches
				Object.keys(this.state.mytouches).forEach(idx => {
					const row = this.state.mytouches[idx];
					if (typeof row === 'object' && typeof row.x === 'number') {
						mytouches.push({ ...{}, ...row });
					}
				});

				if (mytouches.length === 1) {
					distance = getDistance(
						[mytouches[0].x, mytouches[0].y],
						[mytouches[0].newX, mytouches[0].newY]
					);

					if (Math.abs(distance) < maxDriftDistance) {
						// fire longPress
						this.onLongPress(mytouches[0]);
					}
				} else if (mytouches.length === 2) {
					distance = getDistance(
						[mytouches[0].x, mytouches[0].y],
						[mytouches[0].newX, mytouches[0].newY]
					);

					distance2 = getDistance(
						[mytouches[1].x, mytouches[1].y],
						[mytouches[1].newX, mytouches[1].newY]
					);

					if (
						Math.abs(distance) < maxDriftDistance &&
						Math.abs(distance2) < maxDriftDistance
					) {
						// fire longDoublePress
						this.onLongDoublePress(mytouches[0], mytouches[1]);
					}
				}

				// no need to keep touches around now.
				this.untrackTouches();
			}, longPressDuration);
		}

		if (event.cancelable) event.preventDefault();
		event.stopPropagation();

		this.onMousedown(event, null, touches[0].identifier);

		// needed for AC-265 and AC-290
		this.onMousemove(event);

		this.state.lastTouchStart = new Date();
	}

	/** Manually fire onMousemove when onTouchMove event fires */
	onTouchMove(event) {
		this.eventEmitter.emit(eventTypes.TOUCH_MOVE);
		let mytouch = null;

		// find this touch in the running list, and record the new XY.
		// this will be used to measure the overall distance the finger travelled
		// once the longpress timer elapses.
		const changedTouches = event.changedTouches;
		Object.keys(changedTouches).forEach(idx => {
			const row = changedTouches[idx];
			mytouch = this.state.mytouches[row.identifier];

			if (mytouch) {
				mytouch.newX = Math.round(row.pageX - mytouch.offset.left); // xy relative to panel
				mytouch.newY = Math.round(row.pageY - mytouch.offset.top); // xy relative to panel
			}
		});

		try {
			if (event.cancelable) {
				event.preventDefault();
			}
			event.stopPropagation();
		} catch (error) {
			this.warn(error.message);
		}

		if (!mytouch) window.clearTimeout(window.pressTimeout);

		this.onMousemove(event, mytouch);
	}

	/**
	 * Manually fire onMouseup and onClick when onTouchEnd fires
	 * if two onTouchEnd events happen in rapid succession, manually fire onDoubleClick handler
	 */
	onTouchEnd(event) {
		this.eventEmitter.emit(eventTypes.TOUCH_END);
		this.untrackTouches();

		if (event.cancelable) event.preventDefault();
		event.stopPropagation();

		this.onMouseup(event);

		// Create single tap
		this.onClick(event);

		// Detect double tap for editing tools (probably not ideal)
		if (new Date() - this.state.lastTouchEnd <= 300) this.onDoubleClick(event);

		this.state.lastTouchEnd = new Date();
	}

	trackTouch(id, touch) {
		// initialize a place to track current touch collection
		if (!this.state.mytouches) this.state.mytouches = {};

		this.state.mytouches[id] = { ...touch };
	}

	untrackTouches() {
		window.clearTimeout(window.pressTimeout);
		this.state.mytouches = {};
	}

	// remove any existing "state" tools (OHLC crosshair, returns calculator)
	removeStateTools() {
		// remove old tool
		if (this.state.toolOHLCCrosshair) {
			this.state.toolOHLCCrosshair.remove();
			this.state.toolOHLCCrosshair = null;
			this.setCursor(cursorTypes.CROSSHAIR);
		}

		// remove old tool
		if (this.state.toolReturnsCalculator) {
			this.state.toolReturnsCalculator.remove();
			this.state.toolReturnsCalculator = null;
			this.setCursor(cursorTypes.CROSSHAIR);
		}
	}

	onLongPress(touch) {
		this.eventEmitter.emit(eventTypes.LONG_PRESS);
		// remove existing tools
		this.removeStateTools();

		// create OHLC crosshair tool, if enabled
		if (this.params.toolOHLCCrosshairEnabled) {
			// disable zoom/pan temporarily (re-enabled in onClick handler)
			zoomUtil.unregisterZoom(this);

			// create tool
			const tool = new OHLCCrosshairTool(this, {});
			tool.init(this.panels[0], {});

			// create handle
			tool.initHandles([
				tool.createHandle([touch.newX, touch.newY], EdgeHandle)
			]);

			this.panels[0].tools.push(tool);
			this.state.toolOHLCCrosshair = tool;

			tool.onMousedown([touch.newX, touch.newY], touch.id);
		}
	}

	onLongDoublePress(touch1, touch2) {
		this.eventEmitter.emit(eventTypes.LONG_DOUBLE_PRESS);
		// remove existing tools
		this.removeStateTools();

		// create returns calculator tool, if enabled
		if (this.params.toolReturnsCalculatorEnabled) {
			// disable zoom/pan temporarily (re-enabled in onClick handler)
			zoomUtil.unregisterZoom(this);

			// create tool
			const tool = new ReturnsCalculatorTool(this, {});
			tool.init(this.panels[0]);

			// create handles
			tool.handle = [
				tool.createHandle([touch1.newX, touch1.newY], EdgeHandle),
				tool.createHandle([touch2.newX, touch2.newY], EdgeHandle)
			];

			tool.handle[0].state.visible = true;
			tool.handle[1].state.visible = true;

			this.panels[0].tools.push(tool);
			this.state.toolReturnsCalculator = tool;

			this.render();
		}
	}

	// AC-194 Crosshairs Gestural Interactions
	isZoomedSuppressed() {
		return this.state.toolOHLCCrosshair || this.state.toolReturnsCalculator;
	}

	/**
	 * Retrieves currently selected tool
	 * @returns {Tool|null}
	 */
	getSelectedTool() {
		for (const panel of this.panels) {
			for (const tool of panel.tools) {
				if (tool.state.selected) return tool;
			}
		}

		return null;
	}

	/**
	 * Checks to see if mouse coordinates are within a tool
	 * @param {number[]} mouse - Array of mouse coordinates
	 * @returns {Tool|null} Tool over which mouse is hovering
	 */
	_isWithinTool(mouse) {
		for (const panel of this.panels) {
			const mousePanel = [mouse[0], mouse[1] - panel.size.top];

			for (const tool of [...panel.tools].reverse()) {
				// Gets newest tool at this location on panel
				if (tool.isWithin(mousePanel) || tool.isWithinHandle(mousePanel)) return tool;
			}
		}

		return null;
	}

	/** clear out any existing tools and reset the chart state */
	removeTools() {
		this.panels.forEach(panel => {
			[...panel.tools].reverse().forEach(tool => tool.remove());
		});

		this.state.tool = null;
	}

	// remove active tool (currently in progress of drawing) from state
	// One example is this might be called when a tool is in progress and the user cllcks ESC.
	removeActiveTool() {
		if (this.state.tool) {
			this.state.tool.remove();
			this.state.tool = null;
		}
	}

	/** Set or exit tool mode (note: does not affect selected tool state, e.g. if we're exiting tool mode the last tool will still remain in a selected state) */
	setToolMode(tool) {
		if (tool) {
			this.state.tool = tool;
			zoomUtil.unregisterZoom(this);

			if (tool.params && tool.params.isAutomatic) this.onClick(null, { isAutomatic: tool.params.isAutomatic });
			else if (tool.isImmediate) this.onClick(null, { isImmediate: tool.isImmediate });

			if (tool.params) {
				this.eventEmitter.emit(eventTypes.TOOL_ADD, {
					toolType: tool.params && tool.params.id,
					tool
				});
			}
		} else {
			// exit tool mode
			this.state.tool = null;

			zoomUtil.registerZoom(this);

			// render tools
			this.panels.forEach(panel => {
				panel.clearTools();
				panel.renderTools();
			});
		}

		this.eventEmitter.emit(eventTypes.TOOL_MODE_CHANGE, {
			tool,
			isToolMode: this.state.tool !== null
		});
	}

	/**
	 * Selects the specified tool and unselects all others.
	 * If null is passed, it unselects the current tool.
	 * @param {Object|null} newTool - The tool to be selected, or null to unselect.
	 */
	selectTool(newTool = null) {
		// Update the current state with the new tool selection
		this.state.tool = newTool;

		// Emit an event to notify about the tool selection change
		this.eventEmitter.emit(eventTypes.TOOL_SELECT, { tool: newTool });

		// Iterate over each panel to update the tool selection state
		this.panels.forEach(panel => {
			let shouldRerenderTools = false;

			// Check each tool in the panel
			panel.tools.forEach(toolInPanel => {
				// Store the previous selection state of the tool
				const wasPreviouslySelected = toolInPanel.state.selected;

				// Update the selection state: select if it's the new tool, unselect otherwise
				toolInPanel.state.selected = (toolInPanel === newTool);

				// If a tool was previously selected and now is unselected,
				// and no new tool is being selected, mark for re-rendering
				if (!newTool && wasPreviouslySelected && !toolInPanel.state.selected) {
					shouldRerenderTools = true;
				}
			});

			// Re-render tools if needed (e.g., when a tool was unselected without a new selection)
			if (shouldRerenderTools) {
				panel.clearTools();
				panel.renderTools();
			}
		});
	}


	destroy() {
		super.destroy();

		// Remove keyboard event listeners
		document.body.removeEventListener('keydown', this._onKeyDown);
		document.body.removeEventListener('keyup', this._onKeyUp);
	}
}
